import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

const SteamProject = () => {
  const [csrEvents, setCsrEvents] = useState([]);
  const [csrData, setCsrData] = useState([]); // To store CSR titles for mapping
  const [loading, setLoading] = useState(true);
  const [selectedCsrEvent, setSelectedCsrEvent] = useState(null);
  const [selectedCsrDetails, setSelectedCsrDetails] = useState(null); // For CSR details popup
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openCsrDetailsDialog, setOpenCsrDetailsDialog] = useState(false); // For CSR details
  const [csrEventToDelete, setCsrEventToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    csrId: '',
    title: '',
    winners: '',
    description: '',
    sec_description: '',
    image: '',
    document: '',
    video_url: '',
    link: '',
    event_date: new Date().toISOString().split('T')[0] // Default to today's date,
  });

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch CSR events
        const eventsResponse = await getData('proccess.php', 'steam_projects_V2');
        if (eventsResponse.success) {
          setCsrEvents(Array.isArray(eventsResponse.data) ? eventsResponse.data : []);
        } else {
          toast.error(eventsResponse.message || "Failed to load CSR events data");
        }

        // Fetch CSR data for title mapping
        const csrResponse = await getData('proccess.php', 'csr_V2');
        if (csrResponse.success) {
          setCsrData(Array.isArray(csrResponse.data) ? csrResponse.data : []);
        } else {
          toast.error(csrResponse.message || "Failed to load CSR data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch data");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  // Add this effect to reset form when dialogs close
useEffect(() => {
  if (!openEditDialog && !openAddDialog) {
    // Reset form data when dialogs are closed
    setFormData(prev => ({
      ...prev,
      image: '',
      document: ''
    }));
  }
}, [openEditDialog, openAddDialog]);

  const handleAddClick = () => {
    setFormData({
      csrId: '',
      title: '',
      winners: '',
      description: '',
      sec_description: '',
      image: '',
      document: '',
      video_url: '',
      link: '',
      event_date: new Date().toISOString().split('T')[0]
      
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (csrEvent) => {
    setSelectedCsrEvent(csrEvent);
    setFormData({
      csrId: csrEvent.csrId || '',
      title: csrEvent.title || '',
      winners: csrEvent.winners || '',
      description: csrEvent.description || '',
      sec_description: csrEvent.sec_description || '',
      image: csrEvent.image || '',
      document: csrEvent.document || '',
      video_url: csrEvent.video_url || '',
      link: csrEvent.link || '',
      event_date: csrEvent.event_date || new Date().toISOString().split('T')[0]
      
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (csrEvent) => {
    setCsrEventToDelete(csrEvent);
    setOpenDeleteDialog(true);
  };

  const handleCsrTitleClick = (csrId) => {
    const csr = csrData.find(csr => csr.id === csrId);
    setSelectedCsrDetails(csr || null);
    setOpenCsrDetailsDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php', 'steam_projects_V2', csrEventToDelete.id);
      if (response.success) {
        toast.success(`CSR event deleted successfully`);
        setCsrEvents(csrEvents.filter(event => event.id !== csrEventToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete CSR event");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete CSR event");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setCsrEventToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && !['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, or GIF)");
      return;
    }
    setFormData(prev => ({
      ...prev,
      image: file
    }));
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file && !['application/pdf', 'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .doc, .docx
                    'application/vnd.ms-powerpoint',
                    'application/vnd.openxmlformats-officedocument.presentationml.presentation' ].includes(file.type)) {
      toast.error("Please upload a valid document (PDF, DOC, PPT, etc.)");
      return;
    }
    setFormData(prev => ({
      ...prev,
      document: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;

      if (selectedCsrEvent) {
        // Update existing CSR event
        response = await updateData('proccess.php', 'steam_projects_V2', formData, selectedCsrEvent.id);
        if (response.success) {
          toast.success(`CSR event updated successfully`);
          // Update the specific event in the state
          setCsrEvents(prevEvents =>
            prevEvents.map(event =>
              event.id === selectedCsrEvent.id
                ? { ...event, ...formData, image: formData.image && typeof formData.image === 'string' ? formData.image : event.image }
                : event
            )
          );
        }
      } else {
        // Add new CSR event
        response = await addData('proccess.php', 'steam_projects_V2', formData);
        if (response.success) {
          toast.success(`CSR event added successfully`);
          // Assuming response.data is the new event or updated list
          if (Array.isArray(response.data)) {
            setCsrEvents(response.data);
          } else {
            setCsrEvents(prev => [...prev, response.data]);
          }
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedCsrEvent(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    {
      field: "csrId",
      headerName: "Project Title",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const csr = csrData.find(csr => csr.id === params.value);
        return (
          <Typography
            sx={{ color: colors.blue[400], cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => handleCsrTitleClick(params.value)}
          >
            {csr ? csr.title : 'Unknown project'}
          </Typography>
        );
      }
    },
    { field: "title", headerName: "Project Title", flex: 1, minWidth: 120 },
    { field: "winners", headerName: "Winners", flex: 1, minWidth: 120 },
    { field: "description", headerName: "Description", flex: 1, minWidth: 120 },
    { field: "sec_description", headerName: "Secondary Description", flex: 1, minWidth: 120 },
    { field: "video_url", headerName: "Video URL", flex: 1, minWidth: 120 },
    {
  field: "image",
  headerName: "Project Image",
  flex: 1,
  minWidth: 120,
  renderCell: (params) => {
    const imageValue = params.value;
    
    if (!imageValue) {
      return <Typography color={colors.grey[500]}>No image</Typography>;
    }
    
    // If it's a File object (during editing/preview)
    if (imageValue instanceof File) {
      return (
        <Box>
          <Typography color={colors.green[400]} variant="caption">
            New image: {imageValue.name}
          </Typography>
        </Box>
      );
    }
    
    // If it's a string URL (saved image)
    if (typeof imageValue === 'string' && imageValue) {
      return (
        <Box width={50} height={50}>
          <img
            src={`https://mindsinaction.com.na/assets/media/pictures/${imageValue}`}
            alt={imageValue || "CSR Event"}
            style={{ width: 50, height: 50, objectFit: "cover", cursor: 'pointer' }}
            onClick={() => window.open(`https://mindsinaction.com.na/assets/media/pictures/${imageValue}`, "_blank")}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = ''; // or a placeholder image
            }}
          />
        </Box>
      );
    }
    
    return <Typography color={colors.grey[500]}>Invalid image</Typography>;
  }
},

    {
  field: "document",
  headerName: "Project Report",
  flex: 1,
  minWidth: 120,
  renderCell: (params) => {
    // Check if the value is a File object or a string
    const documentValue = params.value;
    
    if (!documentValue) {
      return <Typography color={colors.white[500]}>No document</Typography>;
    }
    
    // If it's a File object (during editing/preview)
    if (documentValue instanceof File) {
      return (
        <Typography color={colors.green[400]}>
          New file selected: {documentValue.name}
        </Typography>
      );
    }
    
    // If it's a string URL (saved document)
    if (typeof documentValue === 'string' && documentValue) {
      return (
        <a
          href={`https://mindsinaction.com.na/assets/document/${documentValue}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: colors.blue[400], textDecoration: 'underline' }}
        >
          View Document
        </a>
      );
    }
    
    return <Typography color={colors.white[500]}>Invalid document</Typography>;
  }
},
    { field: "event_date", headerName: "Event Date", flex: 1, minWidth: 120 },
    { field: "dateCreated", headerName: "Date Created", flex: 1, minWidth: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No project available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedCsrEvent(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedCsrEvent ? "Edit project" : "Add New project"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                select
                style={{ width: "100%" }}
                id="csrId"
                name="csrId"
                label="CSR Project"
                variant="standard"
                value={formData.csrId}
                onChange={handleFormChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                {csrData.map((csr) => (
                  <option key={csr.id} value={csr.id}>
                    {csr.title}
                  </option>
                ))}
              </TextField>
              <TextField
                style={{ width: "100%" }}
                id="title"
                name="title"
                label="Project Title"
                variant="standard"
                value={formData.title}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "100%" }}
                id="winners"
                name="winners"
                label="Winners"
                variant="standard"
                value={formData.winners}
                onChange={handleFormChange}
              />
              <TextField
                style={{ width: "100%" }}
                id="description"
                name="description"
                label="Description"
                multiline
                minRows={4}
                maxRows={12}
                variant="standard"
                value={formData.description}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "100%" }}
                id="sec_description"
                name="sec_description"
                label="Secondary Description"
                multiline
                minRows={4}
                maxRows={12}
                variant="standard"
                value={formData.sec_description}
                onChange={handleFormChange}
              />
              <TextField
                style={{ width: "100%" }}
                id="video_url"
                name="video_url"
                label="Video URL"
                variant="standard"
                value={formData.video_url}
                onChange={handleFormChange}
              />
              <Box width={"100%"}>
                <label>Event Image</label>
                <Input
                  type="file"
                  style={{ width: "100%" }}
                  id="image"
                  name="image"
                  onChange={handleImageChange}
                  required={!selectedCsrEvent}
                />
              </Box>
              <Box width={"100%"}>
                <label>Event Report</label>
                <Input
                  type="file"
                  style={{ width: "100%" }}
                  id="document"
                  name="document"
                  onChange={handleDocumentChange}
                />
              </Box>
              <TextField
                style={{ width: "100%" }}
                id="link"
                name="link"
                label="Link"
                variant="standard"
                value={formData.link}
                onChange={handleFormChange}
              />
              <TextField
                style={{ width: "100%" }}
                id="event_date"
                name="event_date"
                label="Event Date"
                type="date"
                variant="standard"
                value={formData.event_date}
                onChange={handleFormChange}
              />
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedCsrEvent(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedCsrEvent ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSR Details Dialog */}
      <Dialog
        open={openCsrDetailsDialog}
        onClose={() => setOpenCsrDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>project Details</DialogTitle>
        <DialogContent>
          {selectedCsrDetails ? (
            <Box display="flex" flexDirection="column" gap="1rem">
              <Typography><strong>Title:</strong> {selectedCsrDetails.title}</Typography>
              <Typography><strong>Type:</strong> {selectedCsrDetails.csr_type}</Typography>
              <Typography><strong>Description:</strong> {selectedCsrDetails.description}</Typography>
              {selectedCsrDetails.image && (
                <Box>
                  <Typography><strong>Image:</strong></Typography>
                  <img
                    src={`https://mindsinaction.com.na/assets/media/pictures/${selectedCsrDetails.image}`}
                    alt={selectedCsrDetails.title}
                    style={{ width: 200, height: 200, objectFit: "cover" }}
                  />
                </Box>
              )}
              <Typography><strong>Date Created:</strong> {selectedCsrDetails.dateCreated}</Typography>
            </Box>
          ) : (
            <Typography>No project details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCsrDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{csrEventToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            STEAM Projcts Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage STEAM projects
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: colors.blue[400],
            '&:hover': {
              backgroundColor: colors.blue[500],
            }
          }}
        >
          Add Project
        </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "45vh", width: "100%" }} backgroundColor={colors.dark[300]} padding={".2rem"} mt={"1rem"}>
        <DataGrid
          rows={csrEvents}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none"
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default SteamProject;